import { Flow, ManualArea, RackingArea, SingularStationArea } from '@thive/fddm-types';

import { assertNever } from '@/helpers/utils';
import { theme } from '@/modules/common/theme';
import { TemplateType } from '@/modules/common/types/templating';

export const DEFAULT_FLOW_LOADS_AMOUNT = 30;
export const DEFAULT_FLOW_VEHICLE_LIMIT = -1;

export const CONSOLIDATION_FLOW_LINE_COLOR = theme.palette.flows.consolidation;
export const TRANSPORT_FLOW_LINE_COLOR = theme.palette.flows.transport;
export const GOODS_TO_PERSONS_FLOW_LINE_COLOR = theme.palette.flows.goodsToPersons;
export const EXTERNAL_FLOW_LINE_COLOR = theme.palette.flows.external;
export const FLOW_LINE_SELECTED_OPACITY = 100;
export const FLOW_ORDER = ['transport', 'consolidationStorage', 'consolidationRetrieval'];

export const MIN_START_WEIGHT = -1;
export const MAX_START_WEIGHT = 1;
export const MIN_END_WEIGHT = -1;
export const MAX_END_WEIGHT = 1;
export const MIN_RATE = -1;
export const MAX_RATE = 1;

// This is the part of the rack where the shuttle drives. The backend expects the rack with this ID-suffix
export const SHUTTLE_STORAGE_SUFFIX = '/Storage';

export const FLOW_SOURCE_LOAD_HANDLING_TYPES: SingularStationArea['loadHandlingType'][] = [
  'INTAKE',
  'STORAGE',
  'OFF_VEHICLE_PROCESSING',
  'ON_VEHICLE_PROCESSING',
];

export const getValidFlowSourceLoadHandlingTypes = (
  flowType: 'transport_flow' | 'consolidation_flow' | 'external_flow',
): SingularStationArea['loadHandlingType'][] => {
  switch (flowType) {
    case 'transport_flow':
    case 'consolidation_flow':
      return [
        'INTAKE',
        'OFF_VEHICLE_PROCESSING',
        'ON_VEHICLE_PROCESSING',
        'INTAKE_AND_DELIVERY',
        'STORAGE',
      ];
    case 'external_flow':
      return ['DELIVERY'];
    default:
      assertNever(flowType);
  }
};

export const getValidFlowTargetLoadHandlingTypes = (
  flowType: Flow['type'],
): SingularStationArea['loadHandlingType'][] => {
  switch (flowType) {
    case 'TRANSPORT':
      return [
        'DELIVERY',
        'STORAGE',
        'OFF_VEHICLE_PROCESSING',
        'ON_VEHICLE_PROCESSING',
        'INTAKE_AND_DELIVERY',
      ];
    case 'CONSOLIDATION_STORAGE':
      return ['STORAGE'];
    case 'CONSOLIDATION_RETRIEVAL':
      return ['DELIVERY', 'OFF_VEHICLE_PROCESSING', 'ON_VEHICLE_PROCESSING'];
    case 'EXTERNAL':
      return ['INTAKE'];
    case 'GOODS_TO_PERSONS':
      return [];
    default:
      assertNever(flowType);
  }
};

export const getValidFlowSourceRackingLoadHandlingTypes = (
  flowType: 'transport_flow' | 'consolidation_flow' | 'external_flow',
): RackingArea['rackingLoadHandlingType'][] => {
  switch (flowType) {
    case 'transport_flow':
    case 'consolidation_flow':
      return ['INTAKE', 'STORAGE'];
    case 'external_flow':
      return ['DELIVERY'];
    default:
      assertNever(flowType);
  }
};

export const getValidFlowTargetRackingLoadHandlingTypes = (
  flowType: Flow['type'],
): RackingArea['rackingLoadHandlingType'][] => {
  switch (flowType) {
    case 'TRANSPORT':
      return ['DELIVERY', 'STORAGE'];
    case 'CONSOLIDATION_STORAGE':
      return ['STORAGE'];
    case 'CONSOLIDATION_RETRIEVAL':
      return ['INTAKE', 'STORAGE', 'DELIVERY'];
    case 'EXTERNAL':
      return [];
    case 'GOODS_TO_PERSONS':
      return [];
    default:
      assertNever(flowType);
  }
};

export const getValidFlowSourceManualLoadHandlingTypes = (
  flowType: 'transport_flow' | 'consolidation_flow' | 'external_flow',
): ManualArea['loadHandlingType'][] => {
  switch (flowType) {
    case 'transport_flow':
    case 'consolidation_flow':
      return ['INTAKE', 'INTAKE_AND_DELIVERY'];
    case 'external_flow':
      return ['DELIVERY'];
    default:
      assertNever(flowType);
  }
};

export const getValidFlowTargetManualLoadHandlingTypes = (
  flowType: Flow['type'],
): ManualArea['loadHandlingType'][] => {
  switch (flowType) {
    case 'TRANSPORT':
      return ['DELIVERY', 'INTAKE_AND_DELIVERY'];
    case 'CONSOLIDATION_STORAGE':
      return [];
    case 'CONSOLIDATION_RETRIEVAL':
      return ['DELIVERY'];
    case 'EXTERNAL':
      return ['INTAKE'];
    case 'GOODS_TO_PERSONS':
      return [];
    default:
      assertNever(flowType);
  }
};

export const FLOW_SOURCE_TEMPLATE_TYPES: TemplateType[] = [TemplateType.SHUTTLE_RACK];
